import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { useScrollTracking } from '../hooks/useScrollTracking';
import { trackPricingPlanClick, trackCalendlyClick } from '../utils/mixpanel';
const plans = [{
  name: 'Starter',
  monthlyPrice: 3000,
  annualPrice: 30000,
  description: 'Perfect for small teams getting started',
  licenses: 'Up to 5 licenses',
  features: ['Up to 5 user licenses', '1 ERP integration', 'Real-time sync', 'Email support', 'Basic analytics'],
  cta: 'Start Free Trial',
  highlighted: false
}, {
  name: 'Professional',
  monthlyPrice: 5000,
  annualPrice: 48000,
  description: 'Most popular for growing teams',
  licenses: 'Up to 10 licenses',
  features: ['Up to 10 user licenses', '3 ERP integrations', 'Real-time sync', 'Priority support', 'Advanced analytics', 'Custom field mapping', 'API access'],
  cta: 'Start Free Trial',
  highlighted: true
}, {
  name: 'Enterprise',
  monthlyPrice: null,
  annualPrice: null,
  description: 'For large organizations',
  licenses: 'Unlimited licenses',
  features: ['Unlimited user licenses', 'Unlimited ERP integrations', 'Real-time sync', 'Dedicated support', 'Custom analytics', 'Advanced security', 'SLA guarantee', 'Custom onboarding'],
  cta: 'Contact Sales',
  highlighted: false
}];
export function Pricing() {
  const [isAnnual, setIsAnnual] = useState(true);
  const sectionRef = useScrollTracking('pricing');
  const handlePlanClick = (plan: (typeof plans)[0]) => {
    const price = plan.monthlyPrice ? isAnnual ? plan.annualPrice : plan.monthlyPrice : null;
    trackPricingPlanClick(plan.name, isAnnual ? 'annual' : 'monthly', price);
    trackCalendlyClick(plan.cta, 'pricing');
  };
  return <section ref={sectionRef as any} className="py-24 px-6 bg-slate-50" data-id="element-140">
      <div className="max-w-7xl mx-auto" data-id="element-141">
        <div className="text-center mb-12" data-id="element-142">
          <h2 className="text-4xl font-bold text-slate-900 mb-4" data-id="element-143">
            Simple, transparent pricing
          </h2>
          <p className="text-xl text-slate-600 mb-8" data-id="element-144">
            14-day free trial. No credit card required.
          </p>

          {/* Billing toggle */}
          <div className="inline-flex items-center gap-3 bg-white rounded-lg p-1 border border-slate-200" data-id="element-145">
            <button onClick={() => setIsAnnual(false)} className={`px-6 py-2 rounded-md font-semibold transition-all ${!isAnnual ? 'bg-blue-600 text-white shadow-thruline' : 'text-slate-600'}`} data-id="element-146">
              Monthly
            </button>
            <button onClick={() => setIsAnnual(true)} className={`px-6 py-2 rounded-md font-semibold transition-all ${isAnnual ? 'bg-blue-600 text-white shadow-thruline' : 'text-slate-600'}`} data-id="element-147">
              Annual
              <span className="ml-2 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full" data-id="element-148">
                Save 20%
              </span>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8" data-id="element-149">
          {plans.map((plan, index) => <div key={index} className={`bg-white rounded-xl p-8 border-2 ${plan.highlighted ? 'border-blue-600 shadow-xl' : 'border-slate-200'} relative`} data-id="element-150">
              {plan.highlighted && <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold" data-id="element-151">
                  Most Popular
                </div>}

              <div className="mb-6" data-id="element-152">
                <h3 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-153">
                  {plan.name}
                </h3>
                <p className="text-slate-600 text-sm mb-1" data-id="element-154">
                  {plan.description}
                </p>
                <p className="text-blue-600 text-sm font-semibold" data-id="element-155">
                  {plan.licenses}
                </p>
              </div>

              <div className="mb-6" data-id="element-156">
                {plan.monthlyPrice ? <>
                    <div className="flex items-baseline gap-2" data-id="element-157">
                      <span className="text-5xl font-bold text-slate-900" data-id="element-158">
                        $
                        {isAnnual ? (plan.annualPrice / 12).toLocaleString() : plan.monthlyPrice.toLocaleString()}
                      </span>
                      <span className="text-slate-600" data-id="element-159">/month</span>
                    </div>
                    {isAnnual ? <p className="text-sm text-slate-500 mt-1" data-id="element-160">
                        ${plan.annualPrice.toLocaleString()}/year billed
                        annually
                      </p> : <p className="text-sm text-slate-500 mt-1" data-id="element-161">
                        Billed monthly
                      </p>}
                  </> : <div className="text-3xl font-bold text-slate-900" data-id="element-162">
                    Custom
                  </div>}
              </div>

              <a href="https://calendly.com/alex-thruline/learn-about-thruline" target="_blank" rel="noopener noreferrer" onClick={() => handlePlanClick(plan)} className={`block w-full py-3 rounded-lg font-semibold transition-all hover:scale-[1.02] mb-6 text-center ${plan.highlighted ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-thruline' : 'bg-slate-100 text-slate-900 hover:bg-slate-200'}`} data-id="element-163">
                {plan.cta}
              </a>

              <div className="space-y-3" data-id="element-164">
                {plan.features.map((feature, featureIndex) => <div key={featureIndex} className="flex items-start gap-3" data-id="element-165">
                    <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-166" />
                    <span className="text-slate-700" data-id="element-167">{feature}</span>
                  </div>)}
              </div>
            </div>)}
        </div>

        <p className="text-center text-slate-600 mt-12" data-id="element-168">
          All plans include 14-day free trial • Cancel anytime • No setup fees
        </p>
      </div>
    </section>;
}